/**
 * The unit catalog: every unit an owner can pick, mirrored row for row by the `units` table
 * (migration 0019). A parity test (catalog.db.test.ts) fails when the two drift.
 *
 * Factors are exact by definition, never rounded approximations:
 * - the international avoirdupois pound is 453.59237 g, so the ounce is 28.349523125 g and the
 *   stone (14 lb) is 6350.29318 g; US and imperial mass units are identical;
 * - the US gallon is 231 cubic inches (3785.411784 ml); the cup, pint, quart, fluid ounce,
 *   tablespoon and teaspoon are fixed fractions of it; a pinch is 1/16 tsp and a dash 1/8 tsp;
 * - the imperial gallon is 4546.09 ml; the imperial pint is 1/8 of it and the imperial fluid
 *   ounce 1/160 of it.
 *
 * The database stores `to_base` as numeric(20,10), so the longest US spoon factors are rounded
 * there in the 11th decimal place. All arithmetic uses the exact values below.
 */

export const UNIT_SYSTEMS = ['metric', 'us', 'imperial'] as const;
export type UnitSystem = (typeof UNIT_SYSTEMS)[number];

export type UnitKind = 'mass' | 'volume' | 'count';

export interface UnitDefinition {
  /** Stable code stored in the database (`units.code`). */
  readonly code: string;
  readonly name: string;
  /** mass converts to grams, volume to millilitres, count to "each". */
  readonly kind: UnitKind;
  /** How many base units (g, ml or each) one of this unit is. */
  readonly toBase: number;
  /** Measuring systems this unit belongs to, for grouping a picker. */
  readonly systems: readonly UnitSystem[];
  /** Short label for display ("fl oz", "imp pt"). */
  readonly abbr: string;
  readonly sortOrder: number;
}

const US_TSP_ML = 4.92892159375;

export const UNIT_CATALOG = [
  // mass (grams)
  {
    code: 'mg',
    name: 'milligram',
    kind: 'mass',
    toBase: 0.001,
    systems: ['metric'],
    abbr: 'mg',
    sortOrder: 10,
  },
  {
    code: 'g',
    name: 'gram',
    kind: 'mass',
    toBase: 1,
    systems: ['metric'],
    abbr: 'g',
    sortOrder: 20,
  },
  {
    code: 'kg',
    name: 'kilogram',
    kind: 'mass',
    toBase: 1000,
    systems: ['metric'],
    abbr: 'kg',
    sortOrder: 30,
  },
  {
    code: 'oz',
    name: 'ounce',
    kind: 'mass',
    toBase: 28.349523125,
    systems: ['us', 'imperial'],
    abbr: 'oz',
    sortOrder: 40,
  },
  {
    code: 'lb',
    name: 'pound',
    kind: 'mass',
    toBase: 453.59237,
    systems: ['us', 'imperial'],
    abbr: 'lb',
    sortOrder: 50,
  },
  {
    code: 'st',
    name: 'stone',
    kind: 'mass',
    toBase: 6350.29318,
    systems: ['imperial'],
    abbr: 'st',
    sortOrder: 60,
  },

  // volume (millilitres), metric
  {
    code: 'ml',
    name: 'millilitre',
    kind: 'volume',
    toBase: 1,
    systems: ['metric'],
    abbr: 'ml',
    sortOrder: 100,
  },
  {
    code: 'cl',
    name: 'centilitre',
    kind: 'volume',
    toBase: 10,
    systems: ['metric'],
    abbr: 'cl',
    sortOrder: 110,
  },
  {
    code: 'dl',
    name: 'decilitre',
    kind: 'volume',
    toBase: 100,
    systems: ['metric'],
    abbr: 'dl',
    sortOrder: 120,
  },
  {
    code: 'l',
    name: 'litre',
    kind: 'volume',
    toBase: 1000,
    systems: ['metric'],
    abbr: 'l',
    sortOrder: 130,
  },
  {
    code: 'metric_tsp',
    name: 'metric teaspoon',
    kind: 'volume',
    toBase: 5,
    systems: ['metric'],
    abbr: 'metric tsp',
    sortOrder: 140,
  },
  {
    code: 'metric_tbsp',
    name: 'metric tablespoon',
    kind: 'volume',
    toBase: 15,
    systems: ['metric'],
    abbr: 'metric tbsp',
    sortOrder: 150,
  },
  {
    code: 'metric_cup',
    name: 'metric cup',
    kind: 'volume',
    toBase: 250,
    systems: ['metric'],
    abbr: 'metric cup',
    sortOrder: 160,
  },

  // volume, US customary
  {
    code: 'pinch',
    name: 'pinch',
    kind: 'volume',
    toBase: US_TSP_ML / 16,
    systems: ['us'],
    abbr: 'pinch',
    sortOrder: 200,
  },
  {
    code: 'dash',
    name: 'dash',
    kind: 'volume',
    toBase: US_TSP_ML / 8,
    systems: ['us'],
    abbr: 'dash',
    sortOrder: 210,
  },
  {
    code: 'tsp',
    name: 'teaspoon',
    kind: 'volume',
    toBase: US_TSP_ML,
    systems: ['us'],
    abbr: 'tsp',
    sortOrder: 220,
  },
  {
    code: 'tbsp',
    name: 'tablespoon',
    kind: 'volume',
    toBase: 14.78676478125,
    systems: ['us'],
    abbr: 'tbsp',
    sortOrder: 230,
  },
  {
    code: 'floz',
    name: 'fluid ounce',
    kind: 'volume',
    toBase: 29.5735295625,
    systems: ['us'],
    abbr: 'fl oz',
    sortOrder: 240,
  },
  {
    code: 'cup',
    name: 'cup',
    kind: 'volume',
    toBase: 236.5882365,
    systems: ['us'],
    abbr: 'cup',
    sortOrder: 250,
  },
  {
    code: 'pt',
    name: 'pint',
    kind: 'volume',
    toBase: 473.176473,
    systems: ['us'],
    abbr: 'pt',
    sortOrder: 260,
  },
  {
    code: 'qt',
    name: 'quart',
    kind: 'volume',
    toBase: 946.352946,
    systems: ['us'],
    abbr: 'qt',
    sortOrder: 270,
  },
  {
    code: 'gal',
    name: 'gallon',
    kind: 'volume',
    toBase: 3785.411784,
    systems: ['us'],
    abbr: 'gal',
    sortOrder: 280,
  },

  // volume, imperial
  {
    code: 'imp_floz',
    name: 'imperial fluid ounce',
    kind: 'volume',
    toBase: 28.4130625,
    systems: ['imperial'],
    abbr: 'imp fl oz',
    sortOrder: 300,
  },
  {
    code: 'imp_pt',
    name: 'imperial pint',
    kind: 'volume',
    toBase: 568.26125,
    systems: ['imperial'],
    abbr: 'imp pt',
    sortOrder: 310,
  },
  {
    code: 'imp_qt',
    name: 'imperial quart',
    kind: 'volume',
    toBase: 1136.5225,
    systems: ['imperial'],
    abbr: 'imp qt',
    sortOrder: 320,
  },
  {
    code: 'imp_gal',
    name: 'imperial gallon',
    kind: 'volume',
    toBase: 4546.09,
    systems: ['imperial'],
    abbr: 'imp gal',
    sortOrder: 330,
  },

  // count
  {
    code: 'each',
    name: 'each',
    kind: 'count',
    toBase: 1,
    systems: ['metric', 'us', 'imperial'],
    abbr: 'each',
    sortOrder: 400,
  },
  {
    code: 'dozen',
    name: 'dozen',
    kind: 'count',
    toBase: 12,
    systems: ['metric', 'us', 'imperial'],
    abbr: 'dozen',
    sortOrder: 410,
  },
] as const satisfies readonly UnitDefinition[];

export type UnitCode = (typeof UNIT_CATALOG)[number]['code'];

const BY_CODE: ReadonlyMap<string, UnitDefinition> = new Map(
  UNIT_CATALOG.map((u) => [u.code, u] as const),
);

/** The unit with this code, or undefined when the code is not in the catalog. */
export function getUnit(code: string): UnitDefinition | undefined {
  return BY_CODE.get(code);
}

export function isUnitCode(code: string): code is UnitCode {
  return BY_CODE.has(code);
}
